import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { MEASURE } from "@/constants/testIds";
import { Ruler, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useUnits } from "@/lib/useUnits";
import { cmToIn, inToCm } from "@/lib/units";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const FIELDS = [
  { k: "neck_cm", l: "Neck", id: MEASURE.neck, color: "#FF3B30" },
  { k: "chest_cm", l: "Chest", id: MEASURE.chest, color: "#0A84FF" },
  { k: "waist_cm", l: "Waist", id: MEASURE.waist, color: "#30D158" },
  { k: "hips_cm", l: "Hips", id: MEASURE.hips, color: "#FFD60A" },
  { k: "bicep_cm", l: "Bicep", id: MEASURE.bicep, color: "#BF5AF2" },
  { k: "thigh_cm", l: "Thigh", id: MEASURE.thigh, color: "#FF9F0A" },
  { k: "calf_cm", l: "Calf", id: MEASURE.calf, color: "#64D2FF" },
];

const inputClass =
  "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

export default function Measurements() {
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const { isImperial } = useUnits();
  const unitLabel = isImperial ? "in" : "cm";
  // For display: convert stored cm -> in if imperial
  const toDisplay = (cm) => (cm == null ? null : isImperial ? Math.round(cmToIn(cm) * 10) / 10 : cm);
  // For input: convert user-entered value -> cm before persisting
  const toCm = (v) => (v == null || v === "" ? null : isImperial ? inToCm(Number(v)) : Number(v));

  const load = async () => {
    const r = await api.get("/measurements");
    setItems(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => {
    // Form value is in the displayed unit (cm/in); store cm in `form` so the
    // save call doesn't need to know about units.
    setForm((f) => ({ ...f, [k]: v === "" ? null : toCm(v) }));
  };

  const save = async () => {
    const payload = { ...form, note: note || null };
    if (Object.values(payload).every((v) => v == null || v === "")) {
      toast.error("Enter at least one measurement");
      return;
    }
    try {
      await api.post("/measurements", payload);
      toast.success("Measurement logged");
      setForm({});
      setNote("");
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/measurements/${id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const chartData = items.map((m) => {
    const out = { date: new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) };
    for (const f of FIELDS) {
      if (m[f.k] != null) out[f.k] = toDisplay(m[f.k]);
    }
    return out;
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-blaze">/ Measurements</div>
        <h1 className="font-heading text-5xl uppercase mt-1">Body composition.</h1>
      </div>

      <div className="obs-card">
        <div className="flex items-center gap-3 mb-4">
          <Ruler className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Log measurement</div>
            <h3 className="font-heading text-2xl uppercase">Today's numbers ({unitLabel})</h3>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {FIELDS.map((f) => (
            <label key={f.k} className="block">
              <div className="overline mb-1">{f.l}</div>
              <input
                data-testid={f.id}
                type="number"
                step="0.1"
                value={form[f.k] != null ? toDisplay(form[f.k]) : ""}
                onChange={(e) => set(f.k, e.target.value)}
                className={inputClass}
              />
            </label>
          ))}
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (e.g. morning, fasted)"
            className={`${inputClass} flex-1`}
          />
          <button data-testid={MEASURE.save} onClick={save} className="blaze-btn px-5 py-2 text-xs inline-flex items-center gap-2">
            <Save className="h-3 w-3" /> Save
          </button>
        </div>
      </div>

      {chartData.length > 1 && (
        <div className="obs-card">
          <div className="overline mb-1">Trend</div>
          <h3 className="font-heading text-2xl uppercase mb-4">Measurements over time</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A" }} />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }} />
                {FIELDS.map((f) => (
                  <Line key={f.k} type="monotone" dataKey={f.k} name={f.l} stroke={f.color} strokeWidth={2} dot={{ r: 2 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div data-testid={MEASURE.list} className="obs-card">
        <div className="overline mb-2">History</div>
        <h3 className="font-heading text-2xl uppercase mb-4">Entries</h3>
        {items.length === 0 ? (
          <p className="text-zinc-500 text-sm">No entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left py-2 pr-3">Date</th>
                  {FIELDS.map((f) => (
                    <th key={f.k} className="text-left py-2 pr-3">{f.l} ({unitLabel})</th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...items].reverse().map((m) => (
                  <tr key={m.id} className="border-t border-obsidian-600">
                    <td className="py-2 pr-3 text-zinc-400">
                      {new Date(m.created_at).toLocaleDateString()}
                    </td>
                    {FIELDS.map((f) => (
                      <td key={f.k} className="py-2 pr-3 font-data">{m[f.k] != null ? toDisplay(m[f.k]) : "—"}</td>
                    ))}
                    <td className="py-2">
                      <button onClick={() => del(m.id)} className="text-zinc-500 hover:text-blaze">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
